import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaTimes,
  FaRupeeSign,
  FaBullhorn,
  FaTags,
  FaUsers,
  FaBox,
  FaSearch,
  FaMobileAlt,
  FaChartLine,
  FaChevronDown,
  FaChevronUp,
  FaGlobe,
  FaLanguage,
  FaMapMarkerAlt,
  FaBuilding,
  FaInstagram,
  FaYoutube,
  FaArrowLeft,
  FaArrowRight,
  FaPhoneAlt,
  FaCheckCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { brandManagerService } from '../../services/companyAuthService';
import { popularCategories, allCategories, popularCities, allCities, popularLanguages, allLanguages, followerRanges, platformOptions } from '../../../data/categories';


const createBudgetMeta = () => ({
  collaborationType: 'budget',
  amountType: 'total',
  isRange: false,
  amount: '',
  minAmount: '',
  maxAmount: '',
  barterDescription: '',
  currencySymbol: '₹',
});

const createCreatorsMeta = () => ({
  option: '',
  manualCount: '',
});

const createContactDetails = () => ({
  whatsapp: '',
  email: '',
  linkedin: '',
});

const budgetTabs = [
  { key: 'budget', label: 'Budget Collab' },
  { key: 'barter', label: 'Barter Collab' },
];

const budgetScopeOptions = [
  { key: 'total', label: 'Total Campaign' },
  { key: 'perInfluencer', label: 'Per Influencer' },
];

const creatorPresetOptions = [
  'Less than 5',
  '5 - 15',
  '15 - 25',
  '25 - 50',
];

const formatCurrency = (value, symbol = '₹') => {
  if (!value) return '';
  const numeric = value.replace(/[^\d]/g, '');
  if (!numeric) return '';
  return `${symbol} ${Number(numeric).toLocaleString('en-IN')}`;
};

const formatBudgetSummary = (meta) => {
  if (!meta) return '';
  const typeLabel = meta.collaborationType === 'barter' ? 'Barter Collab' : 'Budget Collab';
  if (meta.collaborationType === 'barter') {
    return meta.barterDescription ? `${typeLabel} • ${meta.barterDescription}` : '';
  }

  if (meta.isRange) {
    if (!meta.minAmount || !meta.maxAmount) return '';
    const rangeValue = `${formatCurrency(meta.minAmount, meta.currencySymbol)} - ${formatCurrency(meta.maxAmount, meta.currencySymbol)} • ${meta.amountType === 'total' ? 'Total Campaign' : 'Per Influencer'}`;
    return `${typeLabel} • ${rangeValue}`;
  }

  if (!meta.amount) return '';
  const singleValue = `${formatCurrency(meta.amount, meta.currencySymbol)} • ${meta.amountType === 'total' ? 'Total Campaign' : 'Per Influencer'}`;
  return `${typeLabel} • ${singleValue}`;
};

const formatCreatorsSummary = (meta) => {
  if (!meta) return '';
  if (meta.option && meta.option !== 'Manual') {
    return meta.option;
  }
  if (meta.manualCount) {
    return `${meta.manualCount} creators`;
  }
  return '';
};

const AddAgencyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaign, formData: incomingFormData, requirement: incomingRequirement, focusSection, returnTo, advertisingId, status: incomingStatus, brand } = location.state || {};
  const advertisingStatus = incomingStatus || incomingFormData?.status || 'published';

  // Form state
  const [requirement, setRequirement] = useState(incomingRequirement || '');
  const [expandedSections, setExpandedSections] = useState({});
  const [openModal, setOpenModal] = useState(null);
  const [formData, setFormData] = useState(() => {
    const budgetMeta = { ...createBudgetMeta(), ...(incomingFormData?.budgetMeta || {}) };
    const creatorsMeta = { ...createCreatorsMeta(), ...(incomingFormData?.numberOfCreatorsMeta || {}) };
    const contactDetails = { ...createContactDetails(), ...(incomingFormData?.contactDetails || {}) };
    const incomingBrandManager = incomingFormData?.brandManager || null;
    const incomingBrandManagerId = incomingFormData?.brandManagerId || null;
    // Ensure brandManagerId is set if brandManager exists but brandManagerId doesn't
    const brandManagerId = incomingBrandManagerId || (incomingBrandManager?._id || null);
    return {
      budget: incomingFormData?.budget || formatBudgetSummary(budgetMeta),
      budgetMeta,
      brandName: incomingFormData?.brandName ?? brand?.brandName ?? campaign?.brandId?.brandName ?? '',
      brandManagerId,
      brandManager: incomingBrandManager,
      category: incomingFormData?.category || [],
      numberOfCreators: incomingFormData?.numberOfCreators || formatCreatorsSummary(creatorsMeta),
      numberOfCreatorsMeta: creatorsMeta,
      deliverables: incomingFormData?.deliverables || '',
      agencyName: incomingFormData?.agencyName || '',
      gender: incomingFormData?.gender || '',
      platform: incomingFormData?.platform || [],
      followerRange: incomingFormData?.followerRange || [],
      location: incomingFormData?.location || [],
      language: incomingFormData?.language || [],
      contactDetails,
    };
  });

  // Modal states
  const [searchQuery, setSearchQuery] = useState('');
  const [deliverablesText, setDeliverablesText] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [brandManagers, setBrandManagers] = useState([]);
  const [loadingBrandManagers, setLoadingBrandManagers] = useState(false);

  const maxCharacters = 512;
  const remainingChars = maxCharacters - requirement.length;
  const deliverablesMaxChars = 120;
  const deliverablesRemaining = deliverablesMaxChars - deliverablesText.length;


  const previewRoute = '/company/dashboard/campaigns/advertising/add-agency-details/preview';
  const modalSections = ['platform', 'category', 'language', 'location', 'followerRange', 'deliverables', 'agencyName', 'budget', 'numberOfCreators', 'gender', 'brandManager'];
  const isReturnFlow = Boolean(returnTo);

  // Fetch brand managers on component mount
  useEffect(() => {
    const fetchBrandManagers = async () => {
      try {
        setLoadingBrandManagers(true);
        const response = await brandManagerService.getBrandManagers({ status: 'active' });
        if (response.success && response.data) {
          setBrandManagers(response.data);
        }
      } catch (error) {
        console.error('Error fetching brand managers:', error);
        toast.error('Failed to fetch brand managers');
      } finally {
        setLoadingBrandManagers(false);
      }
    };
    fetchBrandManagers();
  }, [location.key]); //Now whenever you navigate back, brand managers are refreshed.

  // Auto-fill brandName when brand is selected
  useEffect(() => {
    if (brand?.brandName && !formData.brandName) {
      handleInputChange('brandName', brand.brandName);
    }
  }, [brand]);

  // When editing, hydrate brandManager object & contact details from existing brandManagerId
  useEffect(() => {
    // ================================
    // CASE 1: Sync brandManagerId if brandManager exists but brandManagerId is missing
    // This should run immediately, even before brandManagers loads
    // ================================
    if (formData.brandManager && !formData.brandManagerId && formData.brandManager._id) {
      setFormData((prev) => ({
        ...prev,
        brandManagerId: prev.brandManager._id,
      }));
    }

    // ================================
    // CASE 2: Auto-select after create (coming from create page)
    // ================================
    if (!brandManagers.length) {
      return;
    }

    if (
      location.state?.newlyCreatedBrandManagerId &&
      brandManagers.length
    ) {
      const newlyCreatedManager = brandManagers.find(
        (bm) => bm._id === location.state.newlyCreatedBrandManagerId
      );

      if (newlyCreatedManager) {
        // Use setFormData directly instead of handleBrandManagerSelect to avoid opening modal
        setFormData((prev) => {
          const hasWhatsApp = prev.contactDetails?.whatsapp?.trim();
          const hasEmail = prev.contactDetails?.email?.trim();
          const hasLinkedin = prev.contactDetails?.linkedin?.trim();

          const updated = {
            ...prev,
            brandManagerId: newlyCreatedManager._id,
            brandManager: newlyCreatedManager,
            contactDetails: {
              whatsapp: hasWhatsApp ? prev.contactDetails.whatsapp : newlyCreatedManager.phone || prev.contactDetails?.whatsapp || '',
              email: hasEmail ? prev.contactDetails.email : newlyCreatedManager.email || prev.contactDetails?.email || '',
              linkedin: hasLinkedin ? prev.contactDetails.linkedin : newlyCreatedManager.linkedin || prev.contactDetails?.linkedin || '',
            },
          };
          
          return updated;
        });

        // Clear state to prevent re-trigger
        navigate(location.pathname, { replace: true });
        return;
      } else {
      }
    }

    // ================================
    // CASE 3: Normal hydration (edit flow) - match by ID
    // This handles cases where brandManager was passed but we need to sync with the loaded brandManagers list
    // ================================
    const currentBrandManagerId = formData.brandManagerId || formData.brandManager?._id;
    
    if (currentBrandManagerId) {
      // If brandManager is already present and matches the id, ensure brandManagerId is set
      if (formData.brandManager && formData.brandManager._id === currentBrandManagerId) {
        // Ensure brandManagerId is synced
        if (!formData.brandManagerId) {
          setFormData((prev) => ({
            ...prev,
            brandManagerId: prev.brandManager._id,
          }));
        } else {
        }
        return;
      }

      // Find the manager in the brandManagers list
      const existingManager = brandManagers.find(
        (manager) => manager._id === currentBrandManagerId
      );

      if (existingManager) {
        setFormData((prev) => {
          return {
            ...prev,
            brandManager: prev.brandManager && prev.brandManager._id === existingManager._id
              ? prev.brandManager
              : existingManager,
            brandManagerId: existingManager._id, // Ensure brandManagerId is set
            // Always update contact details from the brand manager
            contactDetails: {
              whatsapp: existingManager.phone || '',
              email: existingManager.email || '',
              linkedin: existingManager.linkedin || '',
            },
          };
        });
        return;
      } else {  
      }
    }

    // ================================
    // CASE 4: Match by email if brandManager exists but _id is undefined
    // This handles cases where brandManager was passed without _id (e.g., from navigation state)
    // ================================
    if (formData.brandManager && !formData.brandManagerId && !formData.brandManager._id && formData.brandManager.email) {
      
      const matchedManager = brandManagers.find(
        (bm) => bm.email?.toLowerCase() === formData.brandManager.email?.toLowerCase()
      );

      if (matchedManager) {
        setFormData((prev) => {
          return {
            ...prev,
            brandManagerId: matchedManager._id,
            brandManager: matchedManager,
            // Always update contact details from the matched brand manager
            contactDetails: {
              whatsapp: matchedManager.phone || '',
              email: matchedManager.email || '',
              linkedin: matchedManager.linkedin || '',
            },
          };
        });
        return;
      } else {
      }
    }

    if (!currentBrandManagerId && (!formData.brandManager || !formData.brandManager.email)) {
     
    }
  }, [formData.brandManagerId, brandManagers, formData.brandManager, location.pathname, location.state?.newlyCreatedBrandManagerId, navigate]);

  const requirementRef = useRef(null);
  const [budgetDraft, setBudgetDraft] = useState(() => ({ ...formData.budgetMeta }));
  const [creatorsDraft, setCreatorsDraft] = useState(() => ({ ...formData.numberOfCreatorsMeta }));

  const ensureRequirementPresent = () => {
    if (!requirement.trim()) {
      toast.warning('Please add your requirement details');
      return false;
    }
    return true;
  };

  const ensureBrandManagerPresent = () => {
    // First, check if brandManager exists and has _id
    let brandManagerId = formData.brandManagerId || formData.brandManager?._id;
    
    // If no ID found but brandManager exists with email, try to match by email
    if (!brandManagerId && formData.brandManager?.email && brandManagers.length > 0) {
      const matchedManager = brandManagers.find(
        (bm) => bm.email?.toLowerCase() === formData.brandManager.email?.toLowerCase()
      );
      
      if (matchedManager) {
        brandManagerId = matchedManager._id;
        // Sync the formData immediately with contact details
        setFormData((prev) => ({
          ...prev,
          brandManagerId: matchedManager._id,
          brandManager: matchedManager,
          contactDetails: {
            whatsapp: matchedManager.phone || '',
            email: matchedManager.email || '',
            linkedin: matchedManager.linkedin || '',
          },
        }));
      }
    }
    
    if (!brandManagerId) {
      toast.warning('Please select a brand manager for this campaign');
      return false;
    }
    
    // If brandManagerId is missing but brandManager exists, sync them synchronously
    // This ensures brandManagerId is always set when brandManager exists
    if (!formData.brandManagerId && formData.brandManager?._id) {
      // Use a synchronous update to ensure brandManagerId is set immediately
      setFormData((prev) => {
        if (!prev.brandManagerId && prev.brandManager?._id) {
          return {
            ...prev,
            brandManagerId: prev.brandManager._id,
          };
        }
        return prev;
      });
      // Return true since we have brandManager._id, even if brandManagerId wasn't set yet
      // The setFormData will update it for the next render
      return true;
    }
    
    return true;
  };

  const navigateToPreview = (targetPath = previewRoute, options = {}) => {
    navigate(targetPath, {
      state: { campaign, formData, requirement, advertisingId, status: advertisingStatus },
      ...options,
    });
  };

  const openModalWithPrefill = (section) => {
    if (section === 'deliverables') {
      setDeliverablesText(formData.deliverables || '');
    }
    if(section === 'agencyName'){
      setAgencyName(formData.agencyName || '')
    }
    if (section === 'budget') {
      setBudgetDraft({ ...createBudgetMeta(), ...(formData.budgetMeta || {}) });
    }
    if (section === 'numberOfCreators') {
      setCreatorsDraft({ ...createCreatorsMeta(), ...(formData.numberOfCreatorsMeta || {}) });
    }
    // Sync brandManagerId when opening brandManager modal if brandManager exists but brandManagerId is missing
    // Use functional update to ensure we get the latest state
    if (section === 'brandManager') {
      setFormData((prev) => {
        // If brandManager exists but brandManagerId is missing, sync them
        if (prev.brandManager && !prev.brandManagerId && prev.brandManager._id) {
          return {
            ...prev,
            brandManagerId: prev.brandManager._id,
          };
        }
        return prev;
      });
    }
    setOpenModal(section);
  };

  const toggleSection = (section) => {
    if (modalSections.includes(section)) {
      openModalWithPrefill(section);
    } else {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactDetailsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      contactDetails: {
        ...prev.contactDetails,
        [field]: value,
      },
    }));
  };

  const handleBudgetFieldChange = (field, value) => {
    setBudgetDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Platform selection:
  // - Selecting "All" clears other platforms and selects only "All"
  // - Selecting any specific platform removes "All" and allows multi-select among specifics
  const handlePlatformToggle = (platform) => {
    const currentPlatforms = formData.platform || [];

    if (platform === 'All') {
      // Toggle "All" as a single-source-of-truth option
      if (currentPlatforms.includes('All')) {
        handleInputChange('platform', []);
      } else {
        handleInputChange('platform', ['All']);
      }
      return;
    }

    // For specific platforms, never keep "All" selected
    const withoutAll = currentPlatforms.filter((p) => p !== 'All');

    if (withoutAll.includes(platform)) {
      handleInputChange(
        'platform',
        withoutAll.filter((p) => p !== platform)
      );
    } else {
      handleInputChange('platform', [...withoutAll, platform]);
    }
  };

  const handleCategoryToggle = (category) => {
    const currentCategories = formData.category || [];
    if (currentCategories.includes(category)) {
      handleInputChange('category', currentCategories.filter(c => c !== category));
    } else {
      handleInputChange('category', [...currentCategories, category]);
    }
  };

  const handleLocationToggle = (location) => {
    const currentLocations = formData.location || [];
    if (location === 'Pan India') {
      if (currentLocations.includes('Pan India')) {
        handleInputChange('location', []);
      } else {
        handleInputChange('location', ['Pan India']);
      }
    } else {
      const filtered = currentLocations.filter(l => l !== 'Pan India');
      if (filtered.includes(location)) {
        if (filtered.length <= 5) {
          handleInputChange('location', filtered.filter(l => l !== location));
        }
      } else {
        if (filtered.length < 5) {
          handleInputChange('location', [...filtered, location]);
        } else {
          toast.warning('You can select up to 5 locations');
        }
      }
    }
  };

  const handleLanguageToggle = (language) => {
    const currentLanguages = formData.language || [];
    if (currentLanguages.includes(language)) {
      handleInputChange('language', currentLanguages.filter(l => l !== language));
    } else {
      handleInputChange('language', [...currentLanguages, language]);
    }
  };

  // Follower range behaves like radio buttons:
  // - Only one range can be active at a time (array with 0 or 1 values)
  // - Clicking the already-selected range will clear the selection
  // const handleFollowerRangeToggle = (range) => {
  //   const currentRanges = formData.followerRange || [];

  //   if (currentRanges.includes(range)) {
  //     // Deselect current range
  //     handleInputChange('followerRange', []);
  //   } else {
  //     // Select only this range
  //     handleInputChange('followerRange', [range]);
  //   }
  // };
  const handleFollowerRangeToggle = (range) => {
    const currentRanges = formData.followerRange || [];

    if (currentRanges.includes(range)) {
      // Remove selected range
      handleInputChange(
        'followerRange',
        currentRanges.filter((r) => r !== range)
      );
    } else {
      // Add new range (MULTI SELECT)
      handleInputChange(
        'followerRange',
        [...currentRanges, range]
      );
    }
  };

  const handleDeliverablesSave = () => {
    handleInputChange('deliverables', deliverablesText);
    setOpenModal(null);
  };

  const handleAgencyNameSave = ()=>{
    handleInputChange('agencyName', agencyName);
    setOpenModal(null);
  }

  const isBudgetDraftValid = budgetDraft.collaborationType === 'barter'
    ? Boolean(budgetDraft.barterDescription.trim())
    : budgetDraft.isRange
      ? Boolean(budgetDraft.minAmount && budgetDraft.maxAmount)
      : Boolean(budgetDraft.amount);

  const handleBudgetSave = () => {
    if (!isBudgetDraftValid) {
      toast.warning('Please add your budget details');
      return;
    }
    const summary = formatBudgetSummary(budgetDraft);
    setFormData((prev) => ({
      ...prev,
      budget: summary,
      budgetMeta: { ...budgetDraft },
    }));
    setOpenModal(null);
  };

  const isCreatorsDraftValid = creatorsDraft.option && creatorsDraft.option !== 'Manual'
    ? true
    : Boolean(creatorsDraft.manualCount);

  const handleCreatorsSave = () => {
    if (!isCreatorsDraftValid) {
      toast.warning('Please select the required number of creators');
      return;
    }
    const summary = formatCreatorsSummary(creatorsDraft);
    setFormData((prev) => ({
      ...prev,
      numberOfCreators: summary,
      numberOfCreatorsMeta: { ...creatorsDraft },
    }));
    setOpenModal(null);
  };

  const getFilteredCities = () => {
    if (!searchQuery) return allCities;
    return allCities.filter(city =>
      city.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getFilteredLanguages = () => {
    if (!searchQuery) return allLanguages;
    return allLanguages.filter(lang =>
      lang.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getFilteredCategories = () => {
    if (!searchQuery) return allCategories;
    return allCategories.filter(cat =>
      cat.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const scrollSectionIntoView = (sectionId) => {
    const scrollAction = () => {
      const element = document.getElementById(`section-${sectionId}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scrollAction);
    } else {
      setTimeout(scrollAction, 0);
    }
  };

  useEffect(() => {
    if (!focusSection) return;
    if (focusSection === 'requirement') {
      if (requirementRef.current) {
        requirementRef.current.focus();
        scrollSectionIntoView('requirement');
      }
      return;
    }
    if (modalSections.includes(focusSection)) {
      openModalWithPrefill(focusSection);
      return;
    }
    setExpandedSections((prev) => ({
      ...prev,
      [focusSection]: true,
    }));
    scrollSectionIntoView(focusSection);
  }, [focusSection]);

  const handlePreview = () => {
    if (!ensureRequirementPresent()) {
      return;
    }
    
    if (!ensureBrandManagerPresent()) {
      return;
    }
    
    navigateToPreview();
  };

  const handleDoneEditing = () => {
    if (!ensureRequirementPresent()) {
      return;
    }
    
    if (!ensureBrandManagerPresent()) {
      return;
    }
    
    if (returnTo) {
      navigateToPreview(returnTo, { replace: true });
    } else {
      navigateToPreview();
    }
  };

  const sections = [
    {
      id: 'budget',
      icon: <FaRupeeSign className="text-orange-600" />,
      title: 'Budget',
      description: 'Attracts more interest on the post.',
      hasTrend: true,
      inputType: 'modal',
    },
    {
      id: 'brandName',
      icon: <FaBullhorn className="text-blue-600" />,
      title: 'Brand Name',
      description: 'Get 3x better responses',
      hasTrend: true,
      inputType: 'text',
      placeholder: 'Enter brand name',
      value: formData.brandName,
    },
    {
      id: 'brandManager',
      icon: <FaUsers className="text-indigo-600" />,
      title: 'Brand Manager',
      description: 'Select brand manager for this campaign',
      inputType: 'modal',
    },
    {
      id: 'contactDetails',
      icon: <FaPhoneAlt className="text-green-600" />,
      title: 'Contact Details',
      description: 'WhatsApp, email & LinkedIn',
      inputType: 'contact',
    },
    {
      id: 'category',
      icon: <FaTags className="text-purple-600" />,
      title: 'Category / Topics',
      description: 'Select relevant categories',
      inputType: 'modal',
    },
    {
      id: 'numberOfCreators',
      icon: <FaUsers className="text-green-600" />,
      title: 'Number of creators needed',
      description: 'Specify how many influencers you need',
      inputType: 'modal',
    },
    {
      id: 'deliverables',
      icon: <FaBox className="text-indigo-600" />,
      title: 'Deliverables',
      description: 'What you expect from agencies',
      inputType: 'modal',
    },
    {
      id: 'agencyName',
      icon: <FaBox className="text-indigo-600" />,
      title: 'Agency Name',
      description: 'Write the agency name',
      inputType: 'modal',
    },
    {
      id: 'gender',
      icon: <FaSearch className="text-pink-600" />,
      title: 'Gender',
      description: 'Target audience gender preference',
      inputType: 'modal',
    },
    {
      id: 'platform',
      icon: <FaMobileAlt className="text-red-600" />,
      title: 'Platform',
      description: 'Select social media platforms',
      inputType: 'modal',
    },
    {
      id: 'followerRange',
      icon: <FaChartLine className="text-teal-600" />,
      title: 'Follower range',
      description: 'Preferred influencer follower count',
      inputType: 'modal',
    },
    {
      id: 'location',
      icon: <FaMapMarkerAlt className="text-orange-600" />,
      title: 'Location',
      description: 'Geographic preference for influencers',
      inputType: 'modal',
    },
    {
      id: 'language',
      icon: <FaLanguage className="text-blue-600" />,
      title: 'Language',
      description: 'Preferred content language',
      inputType: 'modal',
    },
  ];

  const getDisplayValue = (sectionId) => {
    switch (sectionId) {
      case 'platform':
        return formData.platform?.length > 0
          ? `${formData.platform.length} selected`
          : 'Select platform';
      case 'category':
        return formData.category?.length > 0
          ? `${formData.category.length} selected`
          : 'Select categories';
      case 'location':
        if (formData.location?.includes('Pan India')) return 'Pan India';
        return formData.location?.length > 0
          ? `${formData.location.length} location${formData.location.length > 1 ? 's' : ''} selected`
          : 'Select location';
      case 'language':
        return formData.language?.length > 0
          ? `${formData.language.length} selected`
          : 'Select language';
      case 'followerRange':
        return formData.followerRange?.length > 0
          ? `${formData.followerRange.length} selected`
          : 'Select follower range';
      case 'deliverables':
        return formData.deliverables || 'Add deliverables';
        case 'agencyName':
          return formData.agencyName || 'Add agencyName';
      case 'budget':
        return formData.budget || '';
      case 'numberOfCreators':
        return formData.numberOfCreators || '';
      case 'gender':
        return formData.gender || '';
      case 'contactDetails':
        if (formData.contactDetails?.whatsapp || formData.contactDetails?.email) {
          return `${formData.contactDetails.whatsapp || ''}${formData.contactDetails.whatsapp && formData.contactDetails.email ? ' • ' : ''}${formData.contactDetails.email || ''}`;
        }
        return 'Add contact info';
      case 'brandManager':
        return formData.brandManager?.name || 'Select brand manager';
      default:
        return '';
    }
  };

  const handleBrandManagerSelect = (manager) => {
    setFormData((prev) => {
      // Always update contact details from the selected brand manager
      const updated = {
        ...prev,
        brandManagerId: manager._id,
        brandManager: manager,
        // Auto-populate contact details from selected brand manager
        contactDetails: {
          whatsapp: manager.phone || '',
          email: manager.email || '',
          linkedin: manager.linkedin || '',
        },
      };
      
      return updated;
    });
    setOpenModal(null);
    setSearchQuery('');
  };

  return (
    <div className="min-h-full lg:bg-gray-50">
      {/* Header */}
      {/* <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 transition-colors p-1"
            aria-label="Close"
          >
            <FaTimes className="text-xl sm:text-2xl" />
          </button>
          <h1 className="text-base sm:text-lg font-semibold text-gray-900">Add details</h1>
          <button
            onClick={handlePreview}
            className="text-sm sm:text-base text-gray-600 hover:text-orange-600 transition-colors font-medium px-2 sm:px-0"
          >
            <span className="hidden sm:inline">Preview →</span>
            <span className="sm:hidden">Preview</span>
          </button>
        </div>
      </div> */}

      <div className="bg-white border-b border-gray-200 sticky top-0 z-[40]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">

          {/* BACK BUTTON */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-700 hover:text-gray-900 transition-colors p-1"
            aria-label="Back"
          >
            <FaArrowLeft className="text-lg sm:text-xl mr-1" />
            <span className="text-sm sm:text-base">Back</span>
          </button>

          {/* EMPTY CENTER (removes Add details text) */}
          <div></div>

          {/* PREVIEW BUTTON */}
          <button
            type="button"
            onClick={isReturnFlow ? handleDoneEditing : handlePreview}
            className="flex items-center text-sm sm:text-base text-orange-500 hover:text-orange-600 transition-colors font-medium"
          >
            <span className="hidden sm:inline">{isReturnFlow ? 'Done' : 'Preview'}</span>
            <span className="sm:hidden">{isReturnFlow ? 'Done' : 'Preview'}</span>
            {!isReturnFlow && <FaArrowRight className="ml-1" />}
          </button>

        </div>
      </div>


      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-8 sm:pb-6">
        {/* Service Type Indicator */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FaBuilding className="text-blue-600 text-lg sm:text-xl" />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs sm:text-sm text-gray-500 mb-1">Looking for</div>
              <div className="text-sm sm:text-base font-medium text-gray-900">
                Influencer Marketing Agencies
              </div>
            </div>
          </div>
        </div>

        {/* Requirement Input Area */}
        <div id="section-requirement" className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
          <textarea
            ref={requirementRef}
            value={requirement}
            onChange={(e) => {
              if (e.target.value.length <= maxCharacters) {
                setRequirement(e.target.value);
              }
            }}
            placeholder="Make your requirement detailed & ensure it is clear — to attract fast, higher-quality responses."
            className="w-full min-h-[180px] sm:min-h-[200px] p-3 sm:p-4 border-none outline-none resize-none text-sm sm:text-base text-gray-700 placeholder-gray-400"
            maxLength={maxCharacters}
          />
          <div className="flex justify-end mt-2">
            <span
              className={`text-xs sm:text-sm ${remainingChars < 50 ? 'text-red-500' : 'text-gray-400'}`}
            >
              {remainingChars}
            </span>
          </div>
        </div>

        {/* Guidance Hint */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-2 sm:p-4 mb-3 sm:mb-6 flex items-start space-x-1.5 sm:space-x-3">
          <div className="bg-green-100 p-1 sm:p-2 rounded-full flex-shrink-0 mt-0.5">
            <svg
              className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <p className="text-[10px] sm:text-sm text-green-800 flex-1 leading-snug sm:leading-relaxed">
            Posts that mention brand, category & budget are 90% more likely to receive responses.
          </p>
        </div>

        {/* Expandable Details Sections */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
            Highlight important details below:
          </h3>

          <div className="space-y-2 sm:space-y-3">
            {sections.map((section) => {
              const isExpanded = expandedSections[section.id];
              const displayValue = getDisplayValue(section.id);
              return (
                <div
                  key={section.id}
                  id={`section-${section.id}`}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md"
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                      <div className="bg-gray-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                        <div className="text-base sm:text-lg">{section.icon}</div>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center space-x-1.5 sm:space-x-2 flex-wrap">
                          <h4 className="font-semibold text-sm sm:text-base text-gray-900">
                            {section.title}
                          </h4>
                          {section.hasTrend && (
                            <FaChartLine className="text-purple-500 text-xs flex-shrink-0" />
                          )}
                        </div>
                        {section.description && !displayValue && (
                          <p className="text-xs text-gray-500 mt-0.5 sm:mt-1 line-clamp-1">
                            {section.description}
                          </p>
                        )}
                        {displayValue && (
                          <p className="text-xs text-orange-600 mt-0.5 sm:mt-1 font-medium">
                            {displayValue}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="ml-2 sm:ml-4 flex-shrink-0">
                      {isExpanded ? (
                        <FaChevronUp className="text-gray-400 text-sm sm:text-base" />
                      ) : (
                        <FaChevronDown className="text-gray-400 text-sm sm:text-base" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && section.inputType !== 'modal' && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-gray-100">
                      {section.inputType === 'select' ? (
                        <select
                          value={formData[section.id] || ''}
                          onChange={(e) => handleInputChange(section.id, e.target.value)}
                          className="w-full mt-3 p-2.5 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                        >
                          <option value="">Select {section.title}</option>
                          {section.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : section.inputType === 'contact' ? (
                        <div className="mt-3 space-y-3">
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">WhatsApp Number *</label>
                            <input
                              type="tel"
                              value={formData.contactDetails?.whatsapp || ''}
                              onChange={(e) => handleContactDetailsChange('whatsapp', e.target.value)}
                              placeholder="+91 1234567890"
                              className="w-full p-2.5 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                              maxLength={20}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">Email *</label>
                            <input
                              type="email"
                              value={formData.contactDetails?.email || ''}
                              onChange={(e) => handleContactDetailsChange('email', e.target.value)}
                              placeholder="you@company.com"
                              className="w-full p-2.5 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-600 block mb-1">LinkedIn (optional)</label>
                            <input
                              type="url"
                              value={formData.contactDetails?.linkedin || ''}
                              onChange={(e) => handleContactDetailsChange('linkedin', e.target.value)}
                              placeholder="https://linkedin.com/in/username"
                              className="w-full p-2.5 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                            />
                          </div>
                        </div>
                      ) : (
                        <input
                          type={section.inputType}
                          value={formData[section.id] || ''}
                          onChange={(e) => handleInputChange(section.id, e.target.value)}
                          placeholder={section.placeholder}
                          className="w-full mt-3 p-2.5 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Inline Preview Button (right aligned, responsive spacing) */}
        <div className="flex justify-end mt-4 sm:mt-6">
          <button
            type="button"
            onClick={isReturnFlow ? handleDoneEditing : handlePreview}
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base transition-colors"
          >
            <span>{isReturnFlow ? 'Done' : 'Preview'}</span>
            {!isReturnFlow && <FaArrowRight className="ml-2 text-xs sm:text-sm" />}
          </button>
        </div>
      </div>

      {/* Budget Modal */}
      {openModal === 'budget' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaRupeeSign className="text-purple-600 text-xl" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Budget</h2>
                  <p className="text-xs text-gray-500">Attracts more interest on the post.</p>
                </div>
              </div>
              <button
                onClick={() => setOpenModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div className="bg-purple-50 rounded-2xl p-1 grid grid-cols-2 gap-1">
                {budgetTabs.map((tab) => {
                  const isActive = budgetDraft.collaborationType === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setBudgetDraft((prev) => ({ ...prev, collaborationType: tab.key }))}
                      className={`flex items-center justify-center rounded-2xl py-2 text-sm font-semibold transition-colors ${isActive ? 'bg-white text-purple-600 shadow' : 'text-gray-500'
                        }`}
                    >
                      {tab.label}
                      {isActive && <span className="ml-1 text-base">✓</span>}
                    </button>
                  );
                })}
              </div>

              {budgetDraft.collaborationType === 'budget' ? (
                <>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Enter budget</label>
                    <button
                      onClick={() => handleBudgetFieldChange('isRange', !budgetDraft.isRange)}
                      className="text-xs font-semibold text-purple-600"
                    >
                      {budgetDraft.isRange ? 'Use single value' : 'Add as range'}
                    </button>
                  </div>
                  {budgetDraft.isRange ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{budgetDraft.currencySymbol}</span>
                        <input
                          type="number"
                          value={budgetDraft.minAmount}
                          onChange={(e) => handleBudgetFieldChange('minAmount', e.target.value)}
                          placeholder="Min budget"
                          className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm"
                        />
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{budgetDraft.currencySymbol}</span>
                        <input
                          type="number"
                          value={budgetDraft.maxAmount}
                          onChange={(e) => handleBudgetFieldChange('maxAmount', e.target.value)}
                          placeholder="Max budget"
                          className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{budgetDraft.currencySymbol}</span>
                      <input
                       onWheel={(e) => e.target.blur()}
                        type="number"
                        value={budgetDraft.amount}
                        onChange={(e) => handleBudgetFieldChange('amount', e.target.value)}
                        placeholder="Enter budget"
                        className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Is the budget for:</p>
                    <div className="space-y-2">
                      {budgetScopeOptions.map((option) => (
                        <label
                          key={option.key}
                          className="flex items-center space-x-3 p-2 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="radio"
                            className="w-4 h-4 text-purple-600"
                            checked={budgetDraft.amountType === option.key}
                            onChange={() => handleBudgetFieldChange('amountType', option.key)}
                          />
                          <span className="text-gray-900 text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Describe the barter offer</label>
                  <textarea
                    value={budgetDraft.barterDescription}
                    onChange={(e) => handleBudgetFieldChange('barterDescription', e.target.value)}
                    placeholder="Free products, services, subscriptions, etc."
                    className="w-full min-h-[120px] p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm resize-none"
                  />
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleBudgetSave}
                disabled={!isBudgetDraftValid}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-colors ${isBudgetDraftValid ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-300 cursor-not-allowed'
                  }`}
              >
                Add Budget
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Number of Creators Modal */}
      {openModal === 'numberOfCreators' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaUsers className="text-purple-600 text-xl" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Required Creators</h2>
                  <p className="text-xs text-gray-500">Pick a range or enter manually</p>
                </div>
              </div>
              <button
                onClick={() => setOpenModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto flex-1">
              {creatorPresetOptions.map((option) => (
                <label
                  key={option}
                  className="flex items-center space-x-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    className="w-4 h-4 text-purple-600"
                    checked={creatorsDraft.option === option}
                    onChange={() => setCreatorsDraft({ option, manualCount: '' })}
                  />
                  <span className="text-gray-900 text-sm">{option}</span>
                </label>
              ))}
              <div className="space-y-2 border border-gray-200 rounded-xl p-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    className="w-4 h-4 text-purple-600"
                    checked={creatorsDraft.option === 'Manual'}
                    onChange={() => setCreatorsDraft((prev) => ({ ...prev, option: 'Manual' }))}
                  />
                  <span className="text-gray-900 text-sm">Enter manually</span>
                </label>
                <input
                  type="number"
                  value={creatorsDraft.manualCount}
                  onChange={(e) => setCreatorsDraft((prev) => ({ ...prev, option: 'Manual', manualCount: e.target.value }))}
                  placeholder="e.g. 100"
                  onWheel={(e) => e.target.blur()}
                  className="w-full mt-2 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleCreatorsSave}
                disabled={!isCreatorsDraftValid}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-colors ${isCreatorsDraftValid ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-300 cursor-not-allowed'
                  }`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Platform Modal */}
      {openModal === 'platform' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaGlobe className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">Platform</h2>
              </div>
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {platformOptions.map((option) => (
                <label
                  key={option.name}
                  className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.platform?.includes(option.name)}
                    onChange={() => handlePlatformToggle(option.name)}
                    className="w-5 h-5 text-orange-500 rounded border-gray-300"
                  />
                  <div className="flex items-center space-x-2">
                    {option.icon}
                    <span className="text-gray-900 font-medium">{option.name}</span>
                  </div>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {openModal === 'category' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaTags className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">Category / Topics</h2>
              </div>
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by category"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Popular</h3>
                  <div className="space-y-2">
                    {popularCategories
                      .filter(cat => !searchQuery || cat.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((category) => (
                        <label
                          key={category}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.category?.includes(category)}
                            onChange={() => handleCategoryToggle(category)}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300"
                          />
                          <span className="text-gray-900">{category}</span>
                        </label>
                      ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">All categories</h3>
                  <div className="space-y-2">
                    {getFilteredCategories()
                      .filter(cat => !popularCategories.includes(cat))
                      .map((category) => (
                        <label
                          key={category}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.category?.includes(category)}
                            onChange={() => handleCategoryToggle(category)}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300"
                          />
                          <span className="text-gray-900">{category}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {openModal === 'location' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-purple-600 text-xl" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Location</h2>
                  <p className="text-xs text-gray-500">Can select up to 5 locations</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by City"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="flex items-start space-x-3 p-3 cursor-pointer hover:bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      checked={formData.location?.includes('Pan India')}
                      onChange={() => handleLocationToggle('Pan India')}
                      className="w-5 h-5 text-orange-500 rounded border-gray-300 mt-0.5"
                    />
                    <div>
                      <span className="text-gray-900 font-medium block">Pan India</span>
                      <span className="text-xs text-gray-500">Anywhere from India</span>
                    </div>
                  </label>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Popular cities</h3>
                  <div className="space-y-2">
                    {popularCities
                      .filter(city => !searchQuery || city.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((city) => (
                        <label
                          key={city}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.location?.includes(city)}
                            onChange={() => handleLocationToggle(city)}
                            disabled={!formData.location?.includes(city) && formData.location?.length >= 5 && !formData.location?.includes('Pan India')}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300 disabled:opacity-50"
                          />
                          <span className="text-gray-900">{city}</span>
                        </label>
                      ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">All cities</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {getFilteredCities()
                      .filter(city => !popularCities.includes(city))
                      .map((city) => (
                        <label
                          key={city}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.location?.includes(city)}
                            onChange={() => handleLocationToggle(city)}
                            disabled={!formData.location?.includes(city) && formData.location?.length >= 5 && !formData.location?.includes('Pan India')}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300 disabled:opacity-50"
                          />
                          <span className="text-gray-900">{city}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Modal */}
      {openModal === 'language' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaLanguage className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">Language</h2>
              </div>
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by language"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Popular</h3>
                  <div className="space-y-2">
                    {popularLanguages
                      .filter(lang => !searchQuery || lang.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((language) => (
                        <label
                          key={language}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.language?.includes(language)}
                            onChange={() => handleLanguageToggle(language)}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300"
                          />
                          <span className="text-gray-900">{language}</span>
                        </label>
                      ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">All languages</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {getFilteredLanguages()
                      .filter(lang => !popularLanguages.includes(lang))
                      .map((language) => (
                        <label
                          key={language}
                          className="flex items-center space-x-3 p-2 cursor-pointer hover:bg-gray-50 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.language?.includes(language)}
                            onChange={() => handleLanguageToggle(language)}
                            className="w-5 h-5 text-orange-500 rounded border-gray-300"
                          />
                          <span className="text-gray-900">{language}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follower Range Modal */}
      {openModal === 'followerRange' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaChartLine className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">Follower Count</h2>
              </div>
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {followerRanges.map((range) => (
                  <label
                    key={range.name}
                    className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={formData.followerRange?.includes(range.name)}
                      onChange={() => handleFollowerRangeToggle(range.name)}
                      className="w-5 h-5 text-orange-500 rounded border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 mb-1">{range.name}</div>
                      <div className="flex items-center space-x-4 text-xs text-gray-600">
                        <div className="flex items-center space-x-1">
                          <FaInstagram className="text-pink-600" />
                          <span>Instagram: {range.instagram}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FaYoutube className="text-red-600" />
                          <span>YouTube: {range.youtube}</span>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deliverables Modal */}
      {openModal === 'deliverables' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaBox className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">What are the deliverables?</h2>
              </div>
              <button
                onClick={() => {
                  setDeliverablesText(formData.deliverables || '');
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <textarea
                value={deliverablesText}
                onChange={(e) => {
                  if (e.target.value.length <= deliverablesMaxChars) {
                    setDeliverablesText(e.target.value);
                  }
                }}
                placeholder="Write your deliverables here..."
                className="w-full min-h-[150px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none text-sm"
                maxLength={deliverablesMaxChars}
              />
              <div className="flex justify-end mt-2">
                <span className={`text-xs ${deliverablesRemaining < 20 ? 'text-red-500' : 'text-gray-400'}`}>
                  {deliverablesRemaining}
                </span>
              </div>
              <div className="mt-4 flex items-start space-x-2 text-sm text-gray-600">
                <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
                </svg>
                <span>Eg: 1 Insta collab reel + 15 Days digital rights</span>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  handleDeliverablesSave();
                  setSearchQuery('');
                }}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Agency Name Modal */}
        {openModal === 'agencyName' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaBox className="text-purple-600 text-xl" />
                <h2 className="text-lg font-semibold text-gray-900">Agency Name</h2>
              </div>
              <button
                onClick={() => {
                  setAgencyName(formData.agencyName || '');
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <textarea
                value={agencyName}
                onChange={(e) => {setAgencyName(e.target.value)}}
                placeholder="Write agency name"
                className="w-full min-h-[150px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none text-sm"
              />
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  handleAgencyNameSave();
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gender Modal */}
      {openModal === 'gender' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaSearch className="text-purple-600 text-xl" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Gender</h2>
                  <p className="text-xs text-gray-500">Target audience gender preference</p>
                </div>
              </div>
              <button
                onClick={() => setOpenModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto flex-1">
              {['Male', 'Female', 'Both'].map((option) => (
                <label
                  key={option}
                  className="flex items-center space-x-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    className="w-4 h-4 text-purple-600"
                    checked={formData.gender === option}
                    onChange={() => handleInputChange('gender', option)}
                  />
                  <span className="text-gray-900 text-sm">{option}</span>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setOpenModal(null)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= BRAND MANAGER MODAL ================= */}
      {openModal === 'brandManager' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">

            {/* ---------- HEADER ---------- */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FaUsers className="text-purple-600 text-xl" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Brand Manager</h2>
                  <p className="text-xs text-gray-500">Select brand manager for this campaign</p>
                </div>
              </div>

              {/* ---------- CLOSE BUTTON ---------- */}
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            {/* ---------- CONTENT ---------- */}
            <div className="p-4 overflow-y-auto flex-1">
              {/* ---------- SEARCH BAR ---------- */}
              <div className="mb-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or position"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
              </div>
              {/* ---------- LOADING STATE ---------- */}
              {loadingBrandManagers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-gray-500 mt-2 text-sm">Loading brand managers...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* BRAND MANAGER LIST */}
                  {brandManagers
                    .filter((manager) => {
                      if (!searchQuery) return true;
                      const query = searchQuery.toLowerCase();
                      return (
                        manager.name?.toLowerCase().includes(query) ||
                        manager.email?.toLowerCase().includes(query) ||
                        manager.position?.toLowerCase().includes(query)
                      );
                    })
                    .map((manager) => {
                      // Check if this manager is selected - check both brandManagerId and brandManager._id
                      const isSelected = formData.brandManagerId === manager._id || formData.brandManager?._id === manager._id;
                      return (
                        <button
                          key={manager._id}
                          onClick={() => handleBrandManagerSelect(manager)}
                          className={`w-full text-left p-3 border rounded-lg transition-colors ${isSelected
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{manager.name}</div>
                              <div className="text-sm text-gray-600">{manager.position}</div>
                              <div className="text-xs text-gray-500">{manager.email}</div>
                            </div>
                            {isSelected && (
                              <FaCheckCircle className="text-orange-500 text-lg" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  {/* ---------- NO BRAND MANAGERS FOUND ---------- */}
                  {brandManagers.filter((manager) => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      manager.name?.toLowerCase().includes(query) ||
                      manager.email?.toLowerCase().includes(query) ||
                      manager.position?.toLowerCase().includes(query)
                    );
                  }).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No brand managers found</p>
                      </div>
                    )}
                  {/* ---------- DIVIDER ---------- */}
                  <div className="border-t my-3"></div>

                  {/* ✅ ADD NEW BRAND MANAGER */}
                  <button
                    onClick={() => {
                      setOpenModal(null);
                      setSearchQuery("");
                      // navigate("/company/dashboard/brands/brand-managers");
                      navigate("/company/dashboard/brands/brand-managers", {
                        state: {
                          from: location.pathname,
                          autoSelectAfterCreate: true
                        }
                      });
                    }}
                    className="w-full text-left px-3 py-3 text-orange-500 font-semibold hover:bg-orange-50 rounded-lg"
                  >
                    + New Brand Manager
                  </button>
                </div>
              )}
            </div>
            {/* ---------- DONE BUTTON ---------- */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setOpenModal(null);
                  setSearchQuery('');
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
     
    </div>
  );
};

export default AddAgencyDetails;
